import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Wallet, TrendUp, Receipt, IdentificationCard, Headset, ListChecks } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get(`${API}/admin/overview`);
        setStats(response.data);
      } catch (error) {
        toast.error('Failed to load overview');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-8"><div className="loader"></div></div>;

  const cards = [
    { label: 'Total Clients', value: stats?.total_clients || 0, icon: Users, color: 'text-brand-accent', bg: 'bg-blue-50' },
    { label: 'Active Clients', value: stats?.active_clients || 0, icon: Users, color: 'text-trading-bullish', bg: 'bg-green-50' },
    { label: 'Trading Accounts', value: stats?.total_accounts || 0, icon: Wallet, color: 'text-brand-secondary', bg: 'bg-indigo-50' },
    { label: 'Total Orders', value: stats?.total_orders || 0, icon: ListChecks, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Open Positions', value: stats?.open_positions || 0, icon: TrendUp, color: 'text-trading-bullish', bg: 'bg-green-50' },
    { label: 'Pending Deposits', value: stats?.pending_deposits || 0, icon: Receipt, color: 'text-trading-warning', bg: 'bg-yellow-50' },
    { label: 'Pending Withdrawals', value: stats?.pending_withdrawals || 0, icon: Receipt, color: 'text-trading-warning', bg: 'bg-yellow-50' },
    { label: 'Pending KYC', value: stats?.pending_kyc || 0, icon: IdentificationCard, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Open Tickets', value: stats?.open_tickets || 0, icon: Headset, color: 'text-brand-accent', bg: 'bg-cyan-50' },
  ];

  return (
    <div className="p-8" data-testid="admin-overview">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-brand-primary">Admin Dashboard</h1>
        <p className="text-slate-600 mt-1">Platform overview and key metrics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-br from-brand-primary to-brand-secondary text-white p-8 rounded-xl">
          <div className="text-sm uppercase tracking-wider text-slate-300 mb-2">Total Balance (AUM)</div>
          <div className="text-4xl font-bold tabular-nums" data-testid="admin-total-balance">
            ${stats?.total_balance?.toFixed(2) || '0.00'}
          </div>
        </div>
        <div className="bg-gradient-to-br from-brand-accent to-blue-600 text-white p-8 rounded-xl">
          <div className="text-sm uppercase tracking-wider text-blue-100 mb-2">Total Equity</div>
          <div className="text-4xl font-bold tabular-nums" data-testid="admin-total-equity">
            ${stats?.total_equity?.toFixed(2) || '0.00'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {cards.map((card, idx) => (
          <div key={idx} className="bg-white p-6 rounded-xl border border-slate-200">
            <div className={`w-12 h-12 rounded-lg ${card.bg} flex items-center justify-center mb-4`}>
              <card.icon size={24} weight="duotone" className={card.color} />
            </div>
            <div className="text-sm text-slate-600 mb-1">{card.label}</div>
            <div className="text-3xl font-bold text-brand-primary tabular-nums">{card.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminOverview;
